import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';

// ── Fallback images (used until DB is populated) ─────────
const FALLBACK = [
  { type: 'image', url: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/whatsapp-image-2026-03-27-at-12.14.07.webp', alt_text: 'IBRC — galeria', position: 'center 0%' },
  { type: 'image', url: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/img_9164.webp', alt_text: 'IBRC — escada', position: 'center 30%' },
  { type: 'image', url: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/img_9233.webp', alt_text: 'IBRC — jardim', position: 'center 40%' },
  { type: 'image', url: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/img_9189.webp', alt_text: 'IBRC — lounge', position: 'center 30%' },
  { type: 'image', url: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/img_0796-hdr.webp', alt_text: 'Estúdio IBRC', position: 'center 40%' },
  { type: 'image', url: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/img_0660-hdr-2.webp', alt_text: 'Produção', position: 'center 50%' },
  { type: 'image', url: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/img_0796-hdr.webp', alt_text: 'A360 set', position: 'center 35%' },
  { type: 'image', url: 'https://uuvsbgxrndwcnudctjlw.supabase.co/storage/v1/object/public/site-images/amplifica_kickoff-2026_-00047.webp', alt_text: 'AmplificaCine', position: 'center 30%' },
];

// ── Ken Burns motion patterns ────────────────────────────
const MOVEMENTS = [
  { scale: [1, 1.15], x: [0, -20], y: [0, -10] },
  { scale: [1.1, 1], x: [10, -10], y: [0, 0] },
  { scale: [1, 1.12], x: [0, 15], y: [-5, 5] },
  { scale: [1.08, 1], x: [-10, 0], y: [5, -5] },
  { scale: [1, 1.1], x: [0, 0], y: [0, -15] },
  { scale: [1.12, 1.02], x: [5, -5], y: [0, 0] },
  { scale: [1, 1.18], x: [-15, 0], y: [0, -8] },
  { scale: [1.1, 1], x: [0, 10], y: [-5, 0] },
];

const INTERVAL = 6000;
const TRANSITION = 1.8;

const HeroCarousel = ({ className = '' }) => {
  const [media, setMedia] = useState(FALLBACK);
  const [current, setCurrent] = useState(0);
  const [loaded, setLoaded] = useState(new Set([0]));
  const videoRef = useRef(null);

  // Fetch from Supabase
  useEffect(() => {
    supabase
      .from('hero_media')
      .select('*')
      .eq('active', true)
      .order('sort_order')
      .then(({ data }) => {
        if (data?.length) setMedia(data);
      });
  }, []);

  // Preload next
  const preload = useCallback((idx) => {
    if (loaded.has(idx)) return;
    const item = media[idx];
    if (!item) return;
    if (item.type === 'image') {
      const img = new Image();
      img.src = item.url;
      img.onload = () => setLoaded((p) => new Set([...p, idx]));
    } else {
      setLoaded((p) => new Set([...p, idx]));
    }
  }, [loaded, media]);

  // Auto-advance
  useEffect(() => {
    if (media.length <= 1) return;
    const next = (current + 1) % media.length;
    preload(next);

    const item = media[current];
    // For videos, wait for video to end instead of fixed interval
    if (item?.type === 'video' && videoRef.current) {
      const handleEnd = () => setCurrent((p) => (p + 1) % media.length);
      const vid = videoRef.current;
      vid.addEventListener('ended', handleEnd);
      return () => vid.removeEventListener('ended', handleEnd);
    }

    const timer = setInterval(() => {
      setCurrent((p) => {
        const n = (p + 1) % media.length;
        preload((n + 1) % media.length);
        return n;
      });
    }, INTERVAL);

    return () => clearInterval(timer);
  }, [current, media, preload]);

  const item = media[current];
  const movement = MOVEMENTS[current % MOVEMENTS.length];

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      <AnimatePresence mode="sync">
        <motion.div
          key={`${current}-${item?.url}`}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: TRANSITION, ease: 'easeInOut' }}
        >
          {item?.type === 'video' ? (
            <video
              ref={videoRef}
              src={item.url}
              className="w-full h-full object-cover grayscale"
              style={{ objectPosition: item.position || 'center' }}
              autoPlay
              muted
              playsInline
              loop={false}
            />
          ) : (
            <motion.img
              src={item?.url}
              alt={item?.alt_text || ''}
              className="w-full h-full object-cover grayscale"
              style={{ objectPosition: item?.position || 'center 40%' }}
              fetchpriority="high"
              width={1200} height={630}
              initial={{ scale: movement.scale[0], x: movement.x[0], y: movement.y[0] }}
              animate={{ scale: movement.scale[1], x: movement.x[1], y: movement.y[1] }}
              transition={{ duration: INTERVAL / 1000, ease: 'linear' }}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* PB + opacity */}
      <div className="absolute inset-0 bg-black/55 z-[1]" />

      {/* Vignette */}
      <div
        className="absolute inset-0 z-[2] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%)' }}
      />

      {/* Top gradient */}
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/60 to-transparent z-[3]" />

      {/* Bottom gradient */}
      <div className="absolute inset-x-0 bottom-0 h-[50%] bg-gradient-to-t from-black/90 via-black/40 to-transparent z-[3]" />

      {/* Progress bars removed — cleaner look */}
    </div>
  );
};

export default HeroCarousel;
